import { createHmac, randomBytes, timingSafeEqual } from "crypto";

const CHANNEL_KEY_SEPARATOR = "::";
const TOKEN_PREFIX = "gencow_ch1";
const DEFAULT_TTL_SECONDS = 300;
const MAX_TTL_SECONDS = 3600;
const MAX_TOKEN_LENGTH = 8192;

export type RealtimeChannelTokenFailure =
  | "malformed"
  | "bad_signature"
  | "app_mismatch"
  | "expired"
  | "invalid_payload";

export interface RealtimeChannelGrant {
  token: string;
  channel: string;
  channelKey: string;
  userId: string;
  expiresAt: number;
  epoch?: string;
}

export interface CreateRealtimeChannelTokenInput {
  appName: string;
  channel: string;
  channelKey?: string;
  args?: unknown;
  userId: string;
  secret: string;
  ttlSeconds?: number;
  nowMs?: number;
  nonce?: string;
  epoch?: string | number;
}

export interface VerifyRealtimeChannelTokenInput {
  appName: string;
  secret: string;
  nowMs?: number;
}

export type VerifyRealtimeChannelTokenResult =
  | {
      ok: true;
      appName: string;
      channel: string;
      channelKey: string;
      args: unknown;
      userId: string;
      epoch?: string;
      issuedAt: number;
      expiresAt: number;
    }
  | { ok: false; reason: RealtimeChannelTokenFailure };

function normalizeForStableJson(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((item) => normalizeForStableJson(item));
  if (typeof value === "object") {
    const source = value as Record<string, unknown>;
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(source).sort()) {
      const normalized = normalizeForStableJson(source[key]);
      if (normalized !== undefined) sorted[key] = normalized;
    }
    return sorted;
  }
  return value;
}

function isEmptyPlainObject(value: unknown): boolean {
  return !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0;
}

function encodeBase64Url(value: string): string {
  return Buffer.from(value, "utf8").toString("base64url");
}

function decodeBase64Url(value: string): string | null {
  try {
    return Buffer.from(value, "base64url").toString("utf8");
  } catch {
    return null;
  }
}

function signSegment(segment: string, secret: string): string {
  return createHmac("sha256", secret).update(segment).digest("base64url");
}

function safeEqual(a: string, b: string): boolean {
  const left = Buffer.from(a);
  const right = Buffer.from(b);
  return left.length === right.length && timingSafeEqual(left, right);
}

function resolveTtlMs(ttlSeconds?: number): number {
  const ttl = Number.isFinite(ttlSeconds) && ttlSeconds! > 0 ? ttlSeconds! : DEFAULT_TTL_SECONDS;
  return Math.floor(Math.min(ttl, MAX_TTL_SECONDS) * 1000);
}

function randomNonce(): string {
  return randomBytes(16).toString("base64url");
}

function validateRequiredString(value: string, label: string): void {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${label} required`);
  }
}

export function normalizeRealtimeChannelEpoch(epoch: string | number | null | undefined): string | undefined {
  if (epoch === undefined || epoch === null) return undefined;
  if (typeof epoch === "number") {
    if (!Number.isFinite(epoch)) throw new Error("epoch must be finite");
    return String(epoch);
  }
  if (typeof epoch !== "string" || epoch.length === 0) {
    throw new Error("epoch must be a non-empty string");
  }
  return epoch;
}

export function buildRealtimeChannelKey(channel: string, args?: unknown): string {
  validateRequiredString(channel, "channel");
  const normalized = normalizeForStableJson(args);
  if (normalized === undefined || isEmptyPlainObject(normalized)) return channel;
  return `${channel}${CHANNEL_KEY_SEPARATOR}${JSON.stringify(normalized)}`;
}

export function createRealtimeChannelToken(input: CreateRealtimeChannelTokenInput): string {
  validateRequiredString(input.appName, "appName");
  validateRequiredString(input.channel, "channel");
  if (input.channelKey !== undefined) validateRequiredString(input.channelKey, "channelKey");
  validateRequiredString(input.userId, "userId");
  validateRequiredString(input.secret, "secret");

  const nowMs = input.nowMs ?? Date.now();
  const args = normalizeForStableJson(input.args);
  const epoch = normalizeRealtimeChannelEpoch(input.epoch);
  const payload = {
    v: 1,
    appName: input.appName,
    channel: input.channel,
    channelKey: input.channelKey ?? buildRealtimeChannelKey(input.channel, args),
    args,
    userId: input.userId,
    ...(epoch ? { epoch } : {}),
    iat: nowMs,
    exp: nowMs + resolveTtlMs(input.ttlSeconds),
    nonce: input.nonce ?? randomNonce(),
  };
  const payloadSegment = encodeBase64Url(JSON.stringify(payload));
  const signature = signSegment(payloadSegment, input.secret);
  return `${TOKEN_PREFIX}.${payloadSegment}.${signature}`;
}

export function verifyRealtimeChannelToken(
  token: string,
  input: VerifyRealtimeChannelTokenInput,
): VerifyRealtimeChannelTokenResult {
  if (typeof token !== "string" || token.length === 0 || token.length > MAX_TOKEN_LENGTH) {
    return { ok: false, reason: "malformed" };
  }
  validateRequiredString(input.appName, "appName");
  validateRequiredString(input.secret, "secret");

  const parts = token.split(".");
  if (parts.length !== 3 || parts[0] !== TOKEN_PREFIX) return { ok: false, reason: "malformed" };
  const [, payloadSegment, signature] = parts;
  if (!safeEqual(signature, signSegment(payloadSegment, input.secret))) {
    return { ok: false, reason: "bad_signature" };
  }

  const decoded = decodeBase64Url(payloadSegment);
  if (!decoded) return { ok: false, reason: "malformed" };

  let payload: any;
  try {
    payload = JSON.parse(decoded);
  } catch {
    return { ok: false, reason: "malformed" };
  }

  if (payload?.v !== 1 || typeof payload.channel !== "string" || typeof payload.userId !== "string") {
    return { ok: false, reason: "invalid_payload" };
  }
  if (payload.appName !== input.appName) return { ok: false, reason: "app_mismatch" };
  if (typeof payload.exp !== "number" || (input.nowMs ?? Date.now()) >= payload.exp) {
    return { ok: false, reason: "expired" };
  }

  if (typeof payload.channelKey !== "string" || payload.channelKey.length === 0) {
    return { ok: false, reason: "invalid_payload" };
  }
  if (payload.epoch !== undefined && (typeof payload.epoch !== "string" || payload.epoch.length === 0)) {
    return { ok: false, reason: "invalid_payload" };
  }

  return {
    ok: true,
    appName: payload.appName,
    channel: payload.channel,
    channelKey: payload.channelKey,
    args: payload.args,
    userId: payload.userId,
    epoch: payload.epoch,
    issuedAt: payload.iat,
    expiresAt: payload.exp,
  };
}
